import moment from 'moment';
import { reaction } from 'mobx';
import { flow, types as t } from 'mobx-state-tree';

import { withEnv } from 'models/withEnv';
import { ApplicationSearch } from 'models/applicantSearch';

export const ApplicantSearchStore = t
  .model('ApplicantSearchStore', {
    applications: t.map(ApplicationSearch),
    loading: true,
    loadingNext: false,
    numResults: 0,
    searchStr: '',
    dateFilter: 'all_time',
    jobId: 'all',
    assignedTo: 'anyone',
    active: true,
    isHired: true,
    isRejected: false,
    isRetracted: false,
    noLabel: true,
    referredBy: true,
    isNotified: true,
    sortBy: 'date',
    sortType: 'desc',
    nextUrl: ''
  })
  .views(withEnv)
  .views(self => ({
    get list() {
      return Array.from(self.applications.values());
    },
    get queryString() {
      let queryString = `?limit=50`;
      if (self.searchStr !== '') {
        queryString += `&search_multi_match=${self.searchStr}`;
      }
      if (self.dateStr !== '') {
        queryString += `&date__gt=now-${self.dateStr}`;
      }
      if (self.active) {
        queryString += `&status=active`;
      }
      if (self.isHired) {
        queryString += `&status=hired`;
      }
      if (self.isRejected) {
        queryString += `&status=rejected`;
      }
      if (self.isRetracted) {
        queryString += `&status=retracted`;
      }
      if (self.noLabel) {
        queryString += `&labels=none`;
      }
      if (self.referredBy) {
        queryString += `&labels=sourced`;
      }
      if (self.isNotified) {
        queryString += `&labels=notified`;
      }
      if (self.jobId !== 'all') {
        queryString += `&job=${self.jobId}`;
      }
      if (self.assignedTo !== 'anyone') {
        if (self.assignedTo === 'none') {
          queryString += `&assigned_to__isnull=true`;
        } else {
          queryString += `&assigned_to=${self.assignedTo}`;
        }
      }
      if (self.sortBy) {
        queryString += `&ordering=${self.sortType === 'desc' ? '-' : ''}${self.sortBy}`;
      }
      return queryString;
    },
    get dateStr() {
      if (self.dateFilter === 'today') {
        const now = moment();
        const midnight = now.clone().startOf('day');
        const minutes = now.diff(midnight, 'minutes');
        return minutes + 'm';
      } else if (self.dateFilter === 'past_seven') {
        return '7d';
      } else if (self.dateFilter === 'past_thirty') {
        return '30d';
      }
      return '';
    }
  }))
  .actions(self => ({
    afterCreate() {
      reaction(
        () => self.searchStr.length,
        () => {
          self.search();
        },
        { delay: 300 }
      );
    },
    setOrdering(value) {
      if (self.sortBy === value) {
        if (self.sortType === 'asc') {
          self.sortType = 'desc';
        } else {
          self.sortType = 'asc';
        }
      } else {
        self.sortBy = value;
      }
      self.search();
    },
    setSearchStr(searchStr) {
      self.searchStr = searchStr;
    },
    search: flow(function*(useNext) {
      self.loading = true;
      self.loadingNext = useNext ? true : false;
      if (
        (!self.active && !self.isHired && !self.isRejected && !self.isRetracted) ||
        (!self.noLabel && !self.referredBy && !self.isNotified)
      ) {
        self.applications.clear();
        self.loading = false;
        return;
      }

      const queryString = useNext ? self.nextUrl : self.queryString;
      const res = yield self.env.api.applications.search(queryString);
      self.nextUrl = res.data.next ? '?' + res.data.next.split('?')[1] : '';
      self.numResults = res.data.count;
      if (!useNext) {
        self.applications.clear();
      }
      res.data.results.forEach(a => {
        self.applications.put(a);
      });
      self.loading = false;
      self.loadingNext = false;

      const applicationsIds = Array.from(self.applications.keys());
      if (applicationsIds.length) {
        self.env.api.applications.loadPictures(applicationsIds).then(res => {
          res.data.forEach((a, index) => {
            setTimeout(() => {
              self.env.applicationPictureStore.setUrl(a.id, a.picture);
            }, index * 2);
          });
        });
      }
    }),
    setDateFilter(value) {
      self.dateFilter = value;
      self.search();
    },
    setJob(value) {
      self.jobId = value.toString();
      self.search();
    },
    setAssignedTo(value) {
      self.assignedTo = value.toString();
      self.search();
    },
    toggleActive() {
      self.active = !self.active;
      self.search();
    },
    toggleHired() {
      self.isHired = !self.isHired;
      self.search();
    },
    toggleRejected() {
      self.isRejected = !self.isRejected;
      self.search();
    },
    toggleRetracted() {
      self.isRetracted = !self.isRetracted;
      self.search();
    },
    toggleNoLabel() {
      self.noLabel = !self.noLabel;
      self.search();
    },
    toggleReferredBy() {
      self.referredBy = !self.referredBy;
      self.search();
    },
    toggleNotified() {
      self.isNotified = !self.isNotified;
      self.search();
    }
  }));
